'use strict'

import {useState, useMemo} from 'react'
import {useShopperBasketsMutation} from '@salesforce/commerce-sdk-react'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useSiteContext} from '../contexts/site'
import {LOYALTYBIRTHDAYBONUS} from '../constants'
import useLoyaltyCalloutsConfigs from './use-loyalty-callouts-configs'
import {useLoyaltyBirthday, getAppliedBirthdayCoupons} from './use-loyalty-birthday'

/**
 *
 * @param {Object} [options]
 * @param {Function} [options.onError] - On error callback
 * @returns {Object} Birthday helper methods
 */
const useBirthdayCoupons = ({onError} = {}) => {
    const {data: basket} = useCurrentBasket()
    const {data: customer} = useCurrentCustomer()
    const {isYR} = useSiteContext()
    const {data: loyaltyCalloutsData} = useLoyaltyCalloutsConfigs(!!customer?.isRegistered, isYR)
    const {showBirthdayCard, redeemableCoupons} = useLoyaltyBirthday(basket, customer)
    const addCouponToBasketMutation = useShopperBasketsMutation('addCouponToBasket')
    const [isApplyingBirthdayCoupons, setIsApplyingBirthdayCoupons] = useState(false)
    const [birthdayApplyError, setBirthdayApplyError] = useState(false)
    const birthdayCalloutHtml = useMemo(() => {
        if (!loyaltyCalloutsData || !redeemableCoupons?.length) return null
        let description = ''
        const {loyaltyBirthdayData, clubOffers} = loyaltyCalloutsData || {}
        if (
            loyaltyBirthdayData?.BA25Redeemable &&
            loyaltyBirthdayData?.BA50Redeemable &&
            clubOffers?.[0]?.loyaltyDescription
        ) {
            description = clubOffers?.[0]?.loyaltyDescription // dynamic value coming from promotion
        } else if (loyaltyBirthdayData?.BA25Redeemable) {
            description =
                'Psst... Celebrate your birthday with 25% off one Fine Jewelry, Demi-Fine Jewelry, Home or Watch item during your birthday month.' // hardcode
        } else if (loyaltyBirthdayData?.BA50Redeemable) {
            description =
                "Psst... Don't forget to celebrate your birthday with 50% off one piece of Fashion Jewelry during your birthday month." // hardcode
        }
        return description
    }, [loyaltyCalloutsData, redeemableCoupons])

    const handleApplyBirthdayCoupons = async () => {
        const applied = getAppliedBirthdayCoupons(basket)
        const toApply = redeemableCoupons.filter((code) => !applied.includes(code))
        if (!basket?.basketId || toApply.length === 0) return
        const hasLoyaltyBonus = basket?.couponItems?.some(
            (item) => item.code === LOYALTYBIRTHDAYBONUS
        )
        const codesToAdd = [...(hasLoyaltyBonus ? [] : [LOYALTYBIRTHDAYBONUS]), ...toApply]
        window.bdayPromosUpdating = true
        setIsApplyingBirthdayCoupons(true)
        setBirthdayApplyError(false)
        try {
            for (const code of codesToAdd) {
                await addCouponToBasketMutation.mutateAsync({
                    parameters: {basketId: basket.basketId},
                    body: {code}
                })
            }
        } catch {
            setBirthdayApplyError(true)
            if (onError) onError()
        } finally {
            window.bdayPromosUpdating = false
            setIsApplyingBirthdayCoupons(false)
            localStorage.setItem('forceOpenPaymentToggle', true)
            setTimeout(() => {
                localStorage.setItem('forceOpenPaymentToggle', false)
            }, 1000)
        }
    }

    return {
        showBirthdayCard,
        redeemableCoupons,
        birthdayCalloutHtml,
        isApplyingBirthdayCoupons,
        birthdayApplyError,
        handleApplyBirthdayCoupons
    }
}

export default useBirthdayCoupons
